import React from "react";

import { Image } from "../common/Image";
import { IStringField } from "../models/formFields";
import { FormInputFieldWrapper } from "./FormInputFieldWrapper";
import { useFormField } from "./hooks/useFormField";

// This is a temporary measure to allow for SSR of FormInputs, @zlebar
export const FormInputWithoutFormatting = <T extends string>(
    props: IStringField<T>,
): React.ReactNode => {
    const {
        type = "text",
        minLength,
        maxLength,
        pattern,
        maxBytes,
        secureImage,
    } = props;

    const field = useFormField<T, HTMLInputElement>({
        ...props,
        type: type,
    });

    const truncateStringByBytes = (
        original: string,
        maxBytesLimit: number,
    ): string => {
        let truncated: string;
        let byteLength: number;
        for (let i = original.length; i >= 0; i--) {
            truncated = original.slice(0, i);
            byteLength = encodeURIComponent(
                truncated.replace(/\d/g, "X"),
            ).replace(/%[A-F\d]{2,6}/g, "U").length;
            if (byteLength <= maxBytesLimit) {
                return truncated;
            }
        }
        throw Error(
            `This should never happen! Somehow an empty string is longer than ${maxBytesLimit} bytes.`,
        );
    };

    const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
        if (!event.currentTarget) {
            return;
        }

        // If maxBytes property is provided, check the current value of the input and truncate it to stay within the size limit.
        if (maxBytes) {
            const original = event.currentTarget.value;
            const truncated = truncateStringByBytes(original, maxBytes);
            if (original !== truncated) {
                event.currentTarget.value = truncated;
            }
        }
    };

    const inputProps = {
        ...field.inputProps,
        minLength,
        maxLength,
        pattern,
        className: props.className ?? "form__field__input",
        onChange: (event: React.FormEvent<HTMLInputElement>) => {
            field.handleChange(event, handleChange);
        },
    };

    const image =
        secureImage?.image &&
        secureImage.image.length > 0 &&
        secureImage.alt ? (
            <Image src={secureImage.image} alt={secureImage.alt} lazy={false} />
        ) : null;

    return (
        <FormInputFieldWrapper
            {...props}
            type={type}
            focused={field.focused}
            isRequired={field.isRequired}
            onLabelClick={field.onLabelClick}
        >
            <input ref={field.inputRef} {...inputProps} />
            {image}
        </FormInputFieldWrapper>
    );
};
